import React, { JSX, lazy, Suspense } from "react";
import type { RouteObject } from "react-router-dom";

import { BolonLanding } from "./bolon/app/BolonLanding";
import { BolonLayout } from "./bolon/app/BolonLayout";
import { RequireDuty } from "./bolon/auth/BackofficeDutyProvider";
import FallbackLoading from "./components/FallbackLoading";
import NotFound from "./components/NotFound";
import User from "./pages/user/user";

/*
 * BOLON backoffice routes, mounted at /bolon under the host's member `User`
 * layout (migration D1, D2, D11). The nested pathless BolonLayout supplies the
 * duty context and the BOLON theme overrides; each leaf is lazy-loaded so the
 * ported feature tree stays out of the main bundle.
 *
 * Source files use named exports; lazy() wants a default.
 */
const page = <T extends () => JSX.Element>(
  load: () => Promise<object>,
  name: string,
) =>
  lazy(() =>
    load().then((module) => ({
      default: (module as Record<string, unknown>)[name] as T,
    })),
  );

const BolonDashboard = page(
  () => import("./bolon/features/bolon/BolonDashboardRoutePage"),
  "BolonDashboardRoutePage",
);
const PersonOrders = page(
  () => import("./bolon/features/bolon/PersonOrdersRoutePage"),
  "PersonOrdersRoutePage",
);
const Customers = page(
  () => import("./bolon/features/customers/CustomersRoutePage"),
  "CustomersRoutePage",
);
const CustomerDetail = page(
  () => import("./bolon/features/customers/CustomerDetailRoutePage"),
  "CustomerDetailRoutePage",
);
const Orders = page(
  () => import("./bolon/features/orders/OrdersRoutePage"),
  "OrdersRoutePage",
);
const OrderForm = page(
  () => import("./bolon/features/orders/OrderFormRoutePage"),
  "OrderFormRoutePage",
);
const OrderDetail = page(
  () => import("./bolon/features/orders/OrderDetailRoutePage"),
  "OrderDetailRoutePage",
);
const Catalog = page(
  () => import("./bolon/features/catalog/CatalogRoutePage"),
  "CatalogRoutePage",
);
const ProductForm = page(
  () => import("./bolon/features/catalog/ProductFormRoutePage"),
  "ProductFormRoutePage",
);
const Brands = page(
  () => import("./bolon/features/catalog/BrandsRoutePage"),
  "BrandsRoutePage",
);
const Categories = page(
  () => import("./bolon/features/catalog/CategoriesRoutePage"),
  "CategoriesRoutePage",
);
const Materials = page(
  () => import("./bolon/features/catalog/MaterialsRoutePage"),
  "MaterialsRoutePage",
);
const Claims = page(
  () => import("./bolon/features/claims/ClaimsRoutePage"),
  "ClaimsRoutePage",
);
const ClaimCreate = page(
  () => import("./bolon/features/claims/ClaimCreateRoutePage"),
  "ClaimCreateRoutePage",
);
const ClaimDetail = page(
  () => import("./bolon/features/claims/ClaimDetailRoutePage"),
  "ClaimDetailRoutePage",
);
const Qr = page(() => import("./bolon/features/qr/QrRoutePage"), "QrRoutePage");
const Achievements = page(
  () =>
    import(
      "./bolon/features/customer-achievements/CustomerAchievementRoutePage"
    ),
  "CustomerAchievementRoutePage",
);
const AchievementDetail = page(
  () =>
    import(
      "./bolon/features/customer-achievements/CustomerAchievementDetailRoutePage"
    ),
  "CustomerAchievementDetailRoutePage",
);
const UserGroups = page(
  () => import("./bolon/features/user-groups/UserGroupsRoutePage"),
  "UserGroupsRoutePage",
);
const Tiers = page(
  () => import("./bolon/features/tiers/TiersRoutePage"),
  "TiersRoutePage",
);
const TierForm = page(
  () => import("./bolon/features/tiers/TierFormRoutePage"),
  "TierFormRoutePage",
);
const CustomTiers = page(
  () => import("./bolon/features/tiers/CustomTierListRoutePage"),
  "CustomTierListRoutePage",
);
const CustomTierForm = page(
  () => import("./bolon/features/tiers/CustomTierFormRoutePage"),
  "CustomTierFormRoutePage",
);
const CustomTierEdit = page(
  () => import("./bolon/features/tiers/CustomTierEditRoutePage"),
  "CustomTierEditRoutePage",
);
const TierGroups = page(
  () => import("./bolon/features/tiers/TierGroupsRoutePage"),
  "TierGroupsRoutePage",
);
const TierGroupDetail = page(
  () => import("./bolon/features/tiers/TierGroupDetailRoutePage"),
  "TierGroupDetailRoutePage",
);

const WithSuspense = (
  Component: React.LazyExoticComponent<() => JSX.Element>,
) => (
  <Suspense fallback={<FallbackLoading />}>
    <Component />
  </Suspense>
);

const gated = (duty: string, element: JSX.Element) => (
  <RequireDuty duty={duty}>{element}</RequireDuty>
);

export const bolonRoutes: RouteObject[] = [
  {
    path: "bolon",
    element: <User />,
    children: [
      {
        element: <BolonLayout />,
        children: [
          {
            /*
             * The landing route, and admin-only since 2026-09-04: the
             * dashboard aggregates SALE VALUE across every sales group, which
             * staff have no reason to see. Non-admins are redirected to
             * Orders by BolonLanding.
             */
            index: true,
            element: (
              <BolonLanding
                dashboard={gated("dashboard", WithSuspense(BolonDashboard))}
              />
            ),
          },
          {
            // Per-sales-group order breakdown, reached by clicking a dashboard
            // row. Gated on "dashboard" like the dashboard itself — it is the
            // same aggregate data, not the Orders feature. The literal segment
            // "unassigned" is a server sentinel for orders belonging to no
            // group, so it arrives here as :groupId too.
            path: "dashboard/:groupId",
            element: gated("dashboard", WithSuspense(PersonOrders)),
          },
          { path: "customers", element: WithSuspense(Customers) },
          {
            path: "customers/:customerId",
            element: WithSuspense(CustomerDetail),
          },
          { path: "orders", element: gated("orders", WithSuspense(Orders)) },
          {
            path: "orders/new",
            element: gated("orders", WithSuspense(OrderForm)),
          },
          {
            path: "orders/:orderId/edit",
            element: gated("orders", WithSuspense(OrderForm)),
          },
          {
            // Declared after orders/new so the literal segment wins; a bare
            // ":orderId" placed first would swallow "new".
            path: "orders/:orderId",
            element: gated("orders", WithSuspense(OrderDetail)),
          },
          { path: "catalog", element: gated("catalog", WithSuspense(Catalog)) },
          {
            path: "catalog/products/new",
            element: gated("catalog", WithSuspense(ProductForm)),
          },
          {
            path: "catalog/products/:productId/edit",
            element: gated("catalog", WithSuspense(ProductForm)),
          },
          {
            path: "catalog/brands",
            element: gated("catalog", WithSuspense(Brands)),
          },
          {
            path: "catalog/categories",
            element: gated("catalog", WithSuspense(Categories)),
          },
          {
            path: "catalog/materials",
            element: gated("catalog", WithSuspense(Materials)),
          },
          { path: "claims", element: gated("claims", WithSuspense(Claims)) },
          {
            path: "claims/new",
            element: gated("claims", WithSuspense(ClaimCreate)),
          },
          {
            // Must stay AFTER "claims/new". React Router ranks static segments
            // above dynamic ones so the order is not load-bearing, but a reader
            // reordering these should know a claim named "new" is unreachable.
            path: "claims/:claimId",
            element: gated("claims", WithSuspense(ClaimDetail)),
          },
          { path: "qr", element: WithSuspense(Qr) },
          {
            // Manager tier since 2026-09-04 — staff (bolon_sales) do not see
            // achievements or tiers. Its own duty rather than `tiers` so the two
            // can be split later without touching the resolver's sets.
            path: "customer-achievements",
            element: gated("achievements", WithSuspense(Achievements)),
          },
          {
            path: "customer-achievements/:ecpCode",
            element: gated("achievements", WithSuspense(AchievementDetail)),
          },
          {
            /*
             * User groups live on the API GATEWAY's own routes, not on a bolon-*
             * service — see features/user-groups/api/userGroupTypes.ts. Gated on
             * its own duty so it can be enabled independently of the rest.
             */
            path: "user-groups",
            element: gated("user-groups", WithSuspense(UserGroups)),
          },
          { path: "tiers", element: gated("tiers", WithSuspense(Tiers)) },
          // Static segments, so "custom" is never read as a `:tierId`, and
          // `tiers/:tierId/edit` is three segments, which these are not.
          {
            path: "tiers/custom",
            element: gated("tiers", WithSuspense(CustomTiers)),
          },
          {
            path: "tiers/custom/new",
            element: gated("tiers", WithSuspense(CustomTierForm)),
          },
          {
            path: "tiers/custom/:tierId/edit",
            element: gated("tiers", WithSuspense(CustomTierEdit)),
          },
          {
            path: "tiers/new",
            element: gated("tiers", WithSuspense(TierForm)),
          },
          {
            path: "tiers/:tierId/edit",
            element: gated("tiers", WithSuspense(TierForm)),
          },
          {
            path: "tier-groups",
            element: gated("tiers", WithSuspense(TierGroups)),
          },
          {
            path: "tier-groups/:ecpCode",
            element: gated("tiers", WithSuspense(TierGroupDetail)),
          },
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
];

export default bolonRoutes;
